import { useState } from 'react';
import type { DebugStep } from './types';

interface StepTimelineProps {
  steps: DebugStep[];
  /** Invoked when user clicks Retry on a failed free step */
  onRetry?: (stepName: string) => void;
  /** If true, renders the Retry button on the failed step. */
  canRetry?: (stepName: string) => boolean;
}

const STATUS_ICON: Record<DebugStep['status'], string> = {
  completed: '✓',
  failed: '✕',
  pending: '○',
  skipped: '—',
};

const STATUS_COLOR: Record<DebugStep['status'], string> = {
  completed: 'text-green-400 border-green-700',
  failed: 'text-red-400 border-red-700',
  pending: 'text-gray-500 border-gray-700',
  skipped: 'text-gray-600 border-gray-800',
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function StepTimeline({ steps, onRetry, canRetry }: StepTimelineProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <ol data-testid="step-timeline" className="space-y-2">
      {steps.map((step) => {
        const isExpanded = expanded === step.name;
        const showRetry = step.status === 'failed' && onRetry && canRetry?.(step.name);
        return (
          <li
            key={step.name}
            data-testid={`step-${step.name}`}
            data-status={step.status}
            className={`border rounded-lg p-3 bg-gray-900 ${STATUS_COLOR[step.status]}`}
          >
            <button
              type="button"
              onClick={() => setExpanded(isExpanded ? null : step.name)}
              className="flex w-full items-center gap-3 text-left"
            >
              <span className="text-lg font-mono" aria-hidden="true">
                {STATUS_ICON[step.status]}
              </span>
              <span className="flex-1 font-mono text-sm text-gray-200">{step.name}</span>
              {step.retryCount > 0 && (
                <span
                  data-testid={`step-retries-${step.name}`}
                  className="text-xs text-yellow-400"
                >
                  retried ×{step.retryCount}
                </span>
              )}
              {step.costUsd > 0 && (
                <span className="font-mono text-xs text-gray-400">
                  ${step.costUsd.toFixed(2)}
                </span>
              )}
              {step.durationMs > 0 && (
                <span className="font-mono text-xs text-gray-400">
                  {formatDuration(step.durationMs)}
                </span>
              )}
            </button>
            {step.error && (
              <div
                data-testid={`step-error-${step.name}`}
                className="mt-2 rounded bg-red-950/50 p-2 font-mono text-xs text-red-300"
              >
                {step.error}
              </div>
            )}
            {isExpanded && step.reviewerResults && step.reviewerResults.length > 0 && (
              <div data-testid={`step-reviewers-${step.name}`} className="mt-3 space-y-1 text-xs text-gray-300">
                {step.reviewerResults.map((r) => (
                  <div key={r.reviewerId} className="flex flex-wrap gap-x-4 font-mono">
                    <span className="text-teal-300">{r.reviewerId}</span>
                    <span className="text-gray-400">{r.provider}/{r.model}</span>
                    <span>in: {r.inputTokens}</span>
                    <span>out: {r.completionTokens}</span>
                    {/* An unpriced model renders as "unknown" rather than
                        crashing on .toFixed(null) — and rather than "$0.00",
                        which would read as a free call. */}
                    <span>{r.costUsd === null ? 'cost unknown' : `$${r.costUsd.toFixed(4)}`}</span>
                    <span>{formatDuration(r.durationMs)}</span>
                    <span>{r.findingsCount} findings</span>
                  </div>
                ))}
              </div>
            )}
            {showRetry && (
              <button
                type="button"
                data-testid={`step-retry-${step.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry(step.name);
                }}
                className="mt-2 rounded bg-yellow-700 px-2 py-1 text-xs text-white hover:bg-yellow-600"
              >
                Retry {step.name}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
